import React from "react";
import ReactDOM from "react-dom";
import "./index.css";
import "./ck-content-styles.scss"
import "react-datepicker/dist/react-datepicker.css";
import App from "./App";
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import reportWebVitals from "./reportWebVitals";
import { BrowserRouter } from "react-router-dom";
import store from "./store";
import { PersistGate } from "redux-persist/integration/react";
import { Provider } from "react-redux";
import { persistStore } from "redux-persist";
import { initializeIcons } from "@fluentui/react";
import RctPageLoader from "./component/RctPageLoader";
import { PostHogProvider} from 'posthog-js/react'
import posthog from 'posthog-js';

// Capacitor imports
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';
import { Camera } from '@capacitor/camera';
import { PushNotificationService } from './services/PushNotificationService';
import { MediaPermissionService } from './services/MediaPermissionService';
import { Preferences } from '@capacitor/preferences';

// Add platform class to html element for native platforms
if (Capacitor.isNativePlatform()) {
  document.documentElement.classList.add('capacitor-platform');
  
  if (Capacitor.getPlatform() === 'android') {
    document.documentElement.classList.add('capacitor-platform-android');
  } else if (Capacitor.getPlatform() === 'ios') {
    document.documentElement.classList.add('capacitor-platform-ios');
  }
  
  console.log('Added platform classes to HTML element:', Capacitor.getPlatform());
}

let persistor = persistStore(store);

// Initialize Capacitor
const initializeCapacitor = async () => {
  // Only initialize on native platforms
  if (Capacitor.isNativePlatform()) {
    // Check network status
    try {
      const status = await Network.getStatus();
      console.log('Network status:', status);
      
      // Add network change listener to monitor connectivity
      Network.addListener('networkStatusChange', (status) => {
        console.log('Network status changed:', status);
      });
      
      // Log API URL for debugging
      console.log('App running in native platform mode');
      console.log('API Base URL:', process.env.REACT_APP_BASE_URL);
    } catch (error) {
      console.error('Error initializing Network:', error);
    }

    // Request camera and microphone permissions
    try {
      console.log('Initializing media permissions...');
      await MediaPermissionService.initializeMediaPermissions();
      console.log('Media permissions initialized successfully');
    } catch (error) {
      console.error('Error initializing media permissions:', error);
    }

    // Initialize push notifications
    try {
      console.log('Initializing push notifications...');
      await PushNotificationService.initializePushNotifications();
      console.log('Push notifications initialized successfully');
      
      // Store auth token in native SharedPreferences for FCM service
      const token = localStorage.getItem('token');
      if (token) {
        console.log('Storing auth token in native SharedPreferences for FCM service');
        try {
          await Preferences.set({
            key: 'AuthToken',
            value: token
          });
          
          // Try to store using any custom plugin if available
          try {
            // Use safer approach to access potential custom plugins
            const customPlugins = Capacitor.getPlatform() === 'android' ? (window as any).PDPreferences : null;
            if (customPlugins && typeof customPlugins.setString === 'function') {
              await customPlugins.setString({
                prefsName: 'PhoneDarlingsPref',
                key: 'AuthToken',
                value: token
              });
              console.log('Stored token using custom PDPreferences plugin');
            }
          } catch (err) {
            console.log('Custom preferences plugin not available, using standard storage');
          }
        } catch (e) {
          console.error('Failed to store token in native SharedPreferences:', e);
        }
      }
    } catch (error) {
      console.error('Error initializing push notifications:', error);
    }
  } else {
    console.log('Running in web mode');
  }
};

// Call the initialization function and handle errors
initializeCapacitor().catch(err => {
  console.error('Error during Capacitor initialization:', err);
});

// Only initialize PostHog if we have a proper key
if (process.env.REACT_APP_PUBLIC_POSTHOG_KEY) {
  posthog.init(
    process.env.REACT_APP_PUBLIC_POSTHOG_KEY,
    {
      api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST || 'https://app.posthog.com',
      person_profiles: 'identified_only',
    }
  );
} else {
  // Create a dummy PostHog instance that won't throw errors
  // This prevents errors in notification handling when app is killed
  Object.assign(posthog, {
    capture: () => {},
    identify: () => {},
    reset: () => {},
    register: () => {},
    onSessionId: () => {},
    getSessionId: () => undefined,
    init: () => posthog // Return self to allow method chaining
  });
}

if (document.getElementById('root') !== undefined) {
  initializeIcons(undefined, {
    disableWarnings: true
  });
  
  ReactDOM.render(
    // <React.StrictMode>
    <BrowserRouter>
    <PostHogProvider client={posthog}>
      <Provider store={store}>
        <PersistGate loading={<RctPageLoader />} persistor={persistor}>
          <App />
        </PersistGate>
      </Provider>
      </PostHogProvider>
    </BrowserRouter>,
  // </React.StrictMode>,
    document.getElementById('root')
  );
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.register();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
